import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff, Loader2, ShieldCheck, LogIn, Sparkles } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { APP_NAME, APP_TAGLINE, APP_FOOTER, GPDP_LOGO, GPDP_LOGO_ALT } from "@/lib/constants";
import { EnvBanner } from "@/components/EnvBanner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showDemo, setShowDemo] = useState(false);
  const [envInfo, setEnvInfo] = useState(null);

  useEffect(() => {
    api.get("/system/env").then((r) => setEnvInfo(r.data)).catch(() => {});
  }, []);
  const showDemoAccounts = envInfo ? envInfo.env === "DEMO" : true;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await login(email.trim().toLowerCase(), password, remember);
      if (result?.mustChangePassword) {
        navigate("/ganti-sandi?first=1");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = (em) => { setEmail(em); setPassword("Simpel@2026"); };

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-5 bg-background" data-testid="login-root">
      <div className="lg:col-span-5">
        <EnvBanner />
      </div>
      {/* ============ LEFT: Brand Hero (desktop only) ============ */}
      <aside className="relative hidden lg:flex lg:col-span-3 flex-col justify-between text-white overflow-hidden bg-navy-hero">
        <div className="absolute inset-0 papua-motif opacity-[0.07] pointer-events-none" />
        {/* Subtle mountain silhouette bottom */}
        <div className="absolute bottom-0 inset-x-0 h-40 opacity-30 pointer-events-none">
          <svg viewBox="0 0 1200 200" preserveAspectRatio="none" className="w-full h-full">
            <path d="M0,200 L0,120 L120,80 L240,140 L360,60 L520,120 L680,40 L820,110 L980,50 L1120,140 L1200,90 L1200,200 Z"
              fill="rgba(255,255,255,0.08)" />
            <path d="M0,200 L0,160 L180,110 L340,150 L480,100 L640,150 L820,90 L980,140 L1120,110 L1200,150 L1200,200 Z"
              fill="rgba(255,255,255,0.05)" />
          </svg>
        </div>

        <div className="relative z-10 p-12">
          <div className="inline-flex items-center gap-3">
            <div className="relative h-14 w-14 rounded-2xl bg-white flex items-center justify-center shadow-2xl ring-4 ring-white/10">
              <img src={GPDP_LOGO} alt={GPDP_LOGO_ALT} className="h-12 w-12 object-contain" data-testid="login-hero-logo" />
            </div>
            <div>
              <div className="text-[0.7rem] tracking-[0.22em] uppercase text-white/70 font-semibold">Klasis Mimika • Papua Tengah</div>
              <div className="font-heading font-black text-2xl leading-none mt-1">{APP_NAME}</div>
            </div>
          </div>
        </div>

        <div className="relative z-10 px-12 max-w-2xl">
          <div className="h-1 w-24 bg-[#E5B52A] rounded-full mb-8" />
          <h1 className="font-heading font-black text-4xl xl:text-[3.25rem] leading-[1.05] text-white">
            Platform Digital<br />
            <span className="text-[#E5B52A]">Pelayanan Gereja</span><br />
            GPDP Klasis Mimika.
          </h1>
          <p className="mt-6 text-base text-white/85 max-w-lg leading-relaxed">
            Sistem manajemen pelayanan terpadu untuk pengurus, jemaat, dan pelayanan Klasis Mimika.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 text-sm text-white/90">
            <Sparkles className="h-4 w-4 text-[#E5B52A]" />
            <em>"{APP_TAGLINE}"</em>
          </div>
          <div className="mt-8 grid grid-cols-3 gap-4 max-w-lg">
            {[
              ["8", "Jemaat"],
              ["1", "Klasis"],
              ["100%", "Digital"],
            ].map(([v, l]) => (
              <div key={l} className="text-center border-l-2 border-[#E5B52A]/60 pl-4 py-1">
                <div className="font-heading font-black text-2xl">{v}</div>
                <div className="text-[0.7rem] tracking-widest uppercase text-white/70 mt-0.5">{l}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 p-12 text-[0.7rem] uppercase tracking-[0.18em] text-white/50 flex items-center gap-2">
          <ShieldCheck className="h-3 w-3" /> {APP_FOOTER}
        </div>
      </aside>

      {/* ============ RIGHT: Form ============ */}
      <div className="lg:col-span-2 flex flex-col p-6 sm:p-10 min-h-screen">
        {/* Mobile brand header */}
        <div className="lg:hidden flex flex-col items-center text-center mb-6 pt-2">
          <div className="h-24 w-24 rounded-full bg-white shadow-lg ring-1 ring-border flex items-center justify-center mb-3">
            <img src={GPDP_LOGO} alt={GPDP_LOGO_ALT} className="h-20 w-20 object-contain" data-testid="login-mobile-logo" />
          </div>
          <div className="font-heading font-black text-xl text-primary tracking-tight">{APP_NAME}</div>
          <div className="text-xs text-muted-foreground mt-1 max-w-[16rem]">Sistem Manajemen Pelayanan<br/>GPDP Klasis Mimika</div>
        </div>

        <div className="my-auto max-w-md w-full mx-auto">
          <div className="text-xs tracking-[0.2em] uppercase text-[#E5B52A] font-bold mb-2">Masuk ke Sistem</div>
          <h2 className="font-heading font-black text-3xl sm:text-4xl text-foreground mb-2 leading-tight">Shalom, Selamat Datang.</h2>
          <p className="text-sm text-muted-foreground mb-6">
            Silakan masuk ke Sistem Manajemen Pelayanan Klasis Mimika – Gereja Pentakosta di Papua.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email / Username</Label>
              <Input
                id="email" type="email" autoComplete="email" required
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="nama@example.local"
                className="h-11"
                data-testid="login-email"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Kata Sandi</Label>
              <div className="relative">
                <Input
                  id="password" type={showPw ? "text" : "password"} autoComplete="current-password" required
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-11 pr-10"
                  data-testid="login-password"
                />
                <button
                  type="button" onClick={() => setShowPw((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-1"
                  data-testid="btn-toggle-password"
                  aria-label="Toggle password"
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)} data-testid="login-remember" />
                <span className="text-muted-foreground">Ingat saya</span>
              </label>
              <Link to="/forgot" className="text-primary hover:text-secondary font-medium hover:underline">Lupa kata sandi?</Link>
            </div>

            {error && (
              <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}

            <Button type="submit" className="w-full h-12 rounded-xl text-base font-semibold shadow-md" disabled={loading} data-testid="btn-login-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <LogIn className="h-4 w-4 mr-2" />}
              {loading ? "Memproses…" : "Masuk"}
            </Button>
          </form>

          {/* Demo accounts collapsible — hanya di environment DEMO */}
          {showDemoAccounts && (
          <div className="mt-6">
            <button
              type="button"
              onClick={() => setShowDemo((v) => !v)}
              className="text-[0.7rem] tracking-[0.18em] uppercase text-muted-foreground hover:text-primary transition-colors"
              data-testid="btn-toggle-demo"
            >
              {showDemo ? "▲ Sembunyikan Akun Demo" : "▼ Tampilkan Akun Demo"}
            </button>
            {showDemo && (
              <div className="mt-3 pt-4 border-t border-border">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {[
                    ["Ketua Klasis", "ketua@example.local"],
                    ["Sekretaris", "sekretaris@example.local"],
                    ["Bendahara", "bendahara@example.local"],
                    ["Ketua BP2K", "bp2k@example.local"],
                    ["Gembala Jemaat", "gembala@example.local"],
                    ["Operator Jemaat", "operator@example.local"],
                  ].map(([label, em]) => (
                    <button
                      key={em} type="button" onClick={() => fillDemo(em)}
                      className="text-left px-3 py-2 rounded-md border border-border hover:border-primary hover:bg-accent/10 transition-colors"
                      data-testid={`demo-fill-${label.toLowerCase().replace(/\s/g,'-')}`}
                    >
                      <div className="font-medium">{label}</div>
                      <div className="text-muted-foreground text-[0.65rem] truncate">{em}</div>
                    </button>
                  ))}
                </div>
                <div className="text-[0.65rem] text-muted-foreground mt-3">Password: <code className="font-mono">Simpel@2026</code></div>
              </div>
            )}
          </div>
          )}
        </div>

        <div className="text-[0.65rem] text-muted-foreground text-center mt-6 space-y-1">
          <Link to="/public" className="hover:text-primary transition-colors font-medium">Kunjungi Portal Publik →</Link>
          <div className="lg:hidden">{APP_FOOTER}</div>
        </div>
      </div>
    </div>
  );
}
